import { Mui, MuiIcons } from "@osu/react-ui";
import { useState, useMemo, useEffect } from 'react';
import {
    useGetStudentRequestReasonsQuery,
    useChangeRequestedMutation,
    useGetScholarsProgramsQuery,
    useGetScholarsPreferencesQuery,
    useSubmitPreferenceChangeRequestMutation,
    useCreateScholarsRequestMutation,
} from '../../services/graphql/api';
import { STUDENT_REQUEST_ACTIONS, PROGRAMS, SCHOLARS_SUB_PROGRAMS } from "../../util/constants";

// Preference-count bounds (mirror the backend rule in scholars-validation.mjs and the detail-page
// editor: ranks 1–3 required, 4–6 optional — i.e. a valid set has 3 to 6 programs).
const MIN_PREFERENCES = 3;
const MAX_PREFERENCES = 6;

const ChangeRequestedDialog = ({ openDialog, setOpenDialog, applicant, isAdmin }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [action, setAction] = useState("");
    const [reason, setReason] = useState("");
    const [caseNumber, setCaseNumber] = useState("");
    const [editRankings, setEditRankings] = useState([]);
    const [alternateOptIn, setAlternateOptIn] = useState(false);
    // Appeal-only: the Scholars sub-program being appealed (a denied applicant may have been denied from
    // several, so the appeal records WHICH one). Required when a Scholars applicant's action is Appeal.
    const [appealSubProgramId, setAppealSubProgramId] = useState("");

    // Only fetch when the dialog is open — the reasons are a Honors-scoped query, so firing it on mount
    // (the dialog is always mounted on the Search page) 401s for Scholars-only staff. Gate like the
    // sibling Scholars queries below.
    const { data: studentRequestReasons } = useGetStudentRequestReasonsQuery(undefined, { skip: !openDialog });
    const [changeRequested, result] = useChangeRequestedMutation();
    const [submitPreferenceChangeRequest] = useSubmitPreferenceChangeRequestMutation();
    // Scholars-specific request creation (appeal / program change / withdraw). The shared Honors
    // changeRequested mutation is left untouched (FR-22); Scholars applicants route here instead.
    const [createScholarsRequest] = useCreateScholarsRequestMutation();

    const isScholarsApplicant = applicant?.programId === PROGRAMS.SCHOLARS.KEY;
    const isPreferenceChange = action === STUDENT_REQUEST_ACTIONS.PREFERENCE_CHANGE.KEY;
    const isAppeal = action === STUDENT_REQUEST_ACTIONS.APPEAL.KEY;
    // Appeal for a Scholars applicant must capture the appealed program up front.
    const isScholarsAppeal = isScholarsApplicant && isAppeal;

    // Appeal destinations: ALL 16 Scholars programs from the static catalog (NOT the term's configured
    // programs) — an appeal may target any program regardless of whether it is open/configured this term.
    // Sorted by label for a stable dropdown.
    const appealProgramOptions = useMemo(
        () => Object.values(SCHOLARS_SUB_PROGRAMS)
            .map(p => ({ subProgramId: p.KEY, label: p.label }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        []
    );

    // Fetch scholars programs when dialog is open and applicant is scholars
    const { data: programs } = useGetScholarsProgramsQuery(undefined, { skip: !openDialog || !isScholarsApplicant });
    const { data: preferences } = useGetScholarsPreferencesQuery(
        { applicantEmployeeId: applicant?.applicantEmployeeId, term: applicant?.term },
        { skip: !openDialog || !isScholarsApplicant || !isPreferenceChange }
    );

    // Build program label lookup
    const programLabels = useMemo(() => (programs ?? []).reduce((acc, p) => {
        acc[p.subProgramId] = p.label || p.subProgramId;
        return acc;
    }, {}), [programs]);

    const isLoadingOrFinished = !!result?.isLoading || !!showSuccess || !!errorMessage;
    const caseNumberIsValid = isAdmin
      ? (caseNumber.length === 0 || /^\d{6,10}$/.test(caseNumber))
      : /^\d{6,10}$/.test(caseNumber);

    const preferenceCountValid = editRankings.length >= MIN_PREFERENCES && editRankings.length <= MAX_PREFERENCES;
    const isFormValid = isPreferenceChange
        ? (reason && caseNumberIsValid && preferenceCountValid)
        : isScholarsAppeal
            ? (action && reason && caseNumberIsValid && appealSubProgramId)
            : (action && reason && caseNumberIsValid);

    // Filter actions: only show Preference Change for Scholars applicants
    const availableActions = Object.values(STUDENT_REQUEST_ACTIONS).filter(opt =>
        opt.KEY !== STUDENT_REQUEST_ACTIONS.PREFERENCE_CHANGE.KEY || isScholarsApplicant
    );

    // Student-request submission side: only OPEN programs may be added to a proposed preference set.
    // Closed programs are intentionally excluded from the picker here (admins still see all programs
    // with open/closed markers on the applicant-detail editor).
    const availableToAdd = (programs ?? []).filter(p =>
        p.isOpen === true && !editRankings.some(r => r.subProgramId === p.subProgramId)
    );

    // Populate rankings when preferences load (handles the async fetch race condition)
    useEffect(() => {
        if (isPreferenceChange && preferences?.length > 0 && editRankings.length === 0) {
            const sorted = [...preferences].sort((a, b) => a.rank - b.rank);
            setEditRankings(sorted.map(p => ({ subProgramId: p.subProgramId, rank: p.rank })));
            // Read alternateOptIn from the first preference item (attached by the backend)
            if (preferences[0]?.alternateOptIn !== undefined) {
                setAlternateOptIn(!!preferences[0].alternateOptIn);
            }
        }
    }, [isPreferenceChange, preferences]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!applicant) { return null; }

    const dialogMessage = `Fill out form to request a change. Applicant ${applicant?.firstName} ${applicant?.lastName} (ID: ${applicant?.applicantEmployeeId}) is currently in the ${applicant?.programId} program.`;

    // Title reflects the chosen action (the dialog is shared across request types). Falls back to a generic
    // label before an action is selected. The selected action's display label drives the "<Label> Request"
    // phrasing (e.g. "Appeal Request", "Program Change Request", "Withdraw Request").
    const selectedActionLabel = Object.values(STUDENT_REQUEST_ACTIONS).find(a => a.KEY === action)?.label;
    const dialogTitle = selectedActionLabel ? `${selectedActionLabel} Request` : "Flag Applicant";

    // Preference ranking helpers
    const moveUp = (index) => {
        if (index === 0) return;
        setEditRankings(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next.map((r, i) => ({ ...r, rank: i + 1 }));
        });
    };
    const moveDown = (index) => {
        setEditRankings(prev => {
            if (index >= prev.length - 1) return prev;
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next.map((r, i) => ({ ...r, rank: i + 1 }));
        });
    };
    const removeRank = (index) => {
        setEditRankings(prev =>
            prev.filter((_, i) => i !== index).map((r, i) => ({ ...r, rank: i + 1 }))
        );
    };
    const addProgram = (subProgramId) => {
        if (editRankings.some(r => r.subProgramId === subProgramId)) return;
        if (editRankings.length >= MAX_PREFERENCES) return;
        setEditRankings(prev => (
            prev.length >= MAX_PREFERENCES ? prev : [...prev, { subProgramId, rank: prev.length + 1 }]
        ));
    };

    // Initialize rankings from current preferences when action changes to preference change
    const handleActionChange = (newAction) => {
        setAction(newAction);
        if (newAction !== STUDENT_REQUEST_ACTIONS.PREFERENCE_CHANGE.KEY) {
            setEditRankings([]);
        }
        // The appealed program only applies to an Appeal; clear it when switching away.
        if (newAction !== STUDENT_REQUEST_ACTIONS.APPEAL.KEY) {
            setAppealSubProgramId("");
        }
    };

    async function confirmAction(e) {
        e.preventDefault();
        e.stopPropagation();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            if (isPreferenceChange) {
                // Submit preference change request
                const input = {
                    applicantEmployeeId: applicant.applicantEmployeeId,
                    term: applicant.term,
                    proposedRankings: editRankings.map(r => ({ subProgramId: r.subProgramId, rank: r.rank })),
                    proposedAlternateOptIn: alternateOptIn,
                    reason,
                    caseNumber: caseNumber || undefined,
                };
                const res = await submitPreferenceChangeRequest({ input });
                if (res.data?.status === "success") {
                    setShowSuccess(true);
                    setErrorMessage("");
                } else {
                    setErrorMessage(res.data?.errorDetails?.[0] || "An error occurred while submitting the preference change request.");
                    setShowSuccess(false);
                }
            } else if (isScholarsApplicant) {
                // Scholars appeal / program change / withdraw — route to the Scholars-specific creation
                // mutation (the Honors changeRequested path is never used for Scholars; FR-22). An appeal
                // additionally sends the appealed Scholars program. caseNumber is a String here (the schema
                // takes String); send undefined when blank so an admin can omit it.
                const res = await createScholarsRequest({
                    applicantEmployeeId: applicant.applicantEmployeeId,
                    term: applicant.term,
                    requestType: action,
                    reason,
                    caseNumber: caseNumber || undefined,
                    subProgramId: isAppeal ? appealSubProgramId : undefined,
                });
                if (res.data?.status === "success") {
                    setShowSuccess(true);
                    setErrorMessage("");
                } else {
                    setErrorMessage(res.data?.errorDetails?.[0] || "An error occurred while processing the request.");
                    setShowSuccess(false);
                }
            } else {
                // Honors change-requested flow (unchanged). caseNumber is an Int here.
                const input = {
                    applicantEmployeeId: applicant.applicantEmployeeId,
                    programId: applicant.programId,
                    term: applicant.term,
                    requestType: action,
                    reason,
                    caseNumber: caseNumber ? parseInt(caseNumber) : undefined,
                };
                const res = await changeRequested({ input });
                if (res.data?.status === "success") {
                    setShowSuccess(true);
                    setErrorMessage("");
                } else {
                    setErrorMessage(res.data?.errorDetails?.[0] || "An error occurred while processing the request.");
                    setShowSuccess(false);
                }
            }
        } catch (error) {
            console.error("Error occurred while requesting change:", error);
            setErrorMessage("An unexpected error occurred.");
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClose = () => {
        setOpenDialog(false);
        setTimeout(() => {
            setShowSuccess(false);
            setErrorMessage("");
            setAction("");
            setReason("");
            setCaseNumber("");
            setEditRankings([]);
            setAlternateOptIn(false);
            setAppealSubProgramId("");
        }, 500);
    };

    return (
        <Mui.Dialog open={openDialog} fullWidth maxWidth={isPreferenceChange ? "md" : "sm"}>
            <Mui.DialogTitle>{dialogTitle}</Mui.DialogTitle>
            {showSuccess && (
                <Mui.Alert severity="success" sx={{ margin: 2, marginBottom: 0 }}>
                    Request submitted successfully!
                </Mui.Alert>
            )}
            {errorMessage && (
                <Mui.Alert severity="error" sx={{ margin: 2, marginBottom: 0 }}>
                    {errorMessage}
                </Mui.Alert>       
            )}
            <Mui.DialogContent>
                {!showSuccess && (
                    <>
                        <Mui.DialogContentText>{dialogMessage}</Mui.DialogContentText>
                        <Mui.FormControl fullWidth sx={{ mt: 2 }}>
                            <Mui.InputLabel id="action-label">Action</Mui.InputLabel>
                            <Mui.Select
                                disabled={isLoadingOrFinished}
                                labelId="action-label"
                                value={action}
                                label="Action"
                                onChange={e => handleActionChange(e.target.value)}
                                required
                            >
                                {availableActions.map(opt => (
                                    <Mui.MenuItem key={opt.KEY} value={opt.KEY}>{opt.label}</Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>

                        {/* Appeal program — shown when a Scholars applicant's action is Appeal. The appeal is
                            filed against a specific Scholars program (a denied applicant may have been denied
                            from several), so the person filing it chooses that program here. All Scholars
                            programs are selectable (open or closed) — an appeal can target any of them. */}
                        {isScholarsAppeal && (
                            <Mui.FormControl fullWidth sx={{ mt: 2 }} required>
                                <Mui.InputLabel id="appeal-program-label">Scholars Program Being Appealed</Mui.InputLabel>
                                <Mui.Select
                                    disabled={isLoadingOrFinished}
                                    labelId="appeal-program-label"
                                    value={appealSubProgramId}
                                    label="Scholars Program Being Appealed"
                                    onChange={e => setAppealSubProgramId(e.target.value)}
                                    required
                                >
                                    {appealProgramOptions.map(p => (
                                        <Mui.MenuItem key={p.subProgramId} value={p.subProgramId}>
                                            {p.label}
                                        </Mui.MenuItem>
                                    ))}
                                </Mui.Select>
                            </Mui.FormControl>
                        )}

                        {/* Preference Change UI — shown when action is Preference Change */}
                        {isPreferenceChange && (
                            <Mui.Box sx={{ mt: 2 }}>
                                <Mui.Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                                    Proposed Program Preferences
                                </Mui.Typography>
                                <Mui.Table size="small" aria-label="Proposed preference rankings">
                                    <Mui.TableHead>
                                        <Mui.TableRow>
                                            <Mui.TableCell sx={{ fontWeight: "bold", width: 60 }}>Rank</Mui.TableCell>
                                            <Mui.TableCell sx={{ fontWeight: "bold" }}>Scholars Program</Mui.TableCell>
                                            <Mui.TableCell sx={{ fontWeight: "bold", width: 140 }}>Actions</Mui.TableCell>
                                        </Mui.TableRow>
                                    </Mui.TableHead>
                                    <Mui.TableBody>
                                        {editRankings.map((r, index) => (
                                            <Mui.TableRow key={r.subProgramId}>
                                                <Mui.TableCell>{r.rank}</Mui.TableCell>
                                                <Mui.TableCell>{programLabels[r.subProgramId] || r.subProgramId}</Mui.TableCell>
                                                <Mui.TableCell>
                                                    <Mui.IconButton size="small" onClick={() => moveUp(index)} disabled={index === 0 || isSubmitting} aria-label="Move up">
                                                        <MuiIcons.ArrowUpward fontSize="small" />
                                                    </Mui.IconButton>
                                                    <Mui.IconButton size="small" onClick={() => moveDown(index)} disabled={index === editRankings.length - 1 || isSubmitting} aria-label="Move down">
                                                        <MuiIcons.ArrowDownward fontSize="small" />
                                                    </Mui.IconButton>
                                                    <Mui.IconButton size="small" onClick={() => removeRank(index)} color="error" disabled={isSubmitting} aria-label="Remove">
                                                        <MuiIcons.Close fontSize="small" />
                                                    </Mui.IconButton>
                                                </Mui.TableCell>
                                            </Mui.TableRow>
                                        ))}
                                        {editRankings.length === 0 && (
                                            <Mui.TableRow>
                                                <Mui.TableCell colSpan={3} align="center">
                                                    <Mui.Typography variant="body2" color="text.secondary">No programs selected</Mui.Typography>
                                                </Mui.TableCell>
                                            </Mui.TableRow>
                                        )}
                                    </Mui.TableBody>
                                </Mui.Table>

                                {editRankings.length > 0 && editRankings.length < MIN_PREFERENCES && (
                                    <Mui.Alert severity="warning" sx={{ mt: 2 }}>
                                        At least {MIN_PREFERENCES} program preferences are required — you currently have {editRankings.length}. Add {MIN_PREFERENCES - editRankings.length} more before submitting.
                                    </Mui.Alert>
                                )}

                                {editRankings.length >= MAX_PREFERENCES ? (
                                    <Mui.Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        Maximum of {MAX_PREFERENCES} preferences reached.
                                    </Mui.Typography>
                                ) : availableToAdd.length > 0 && (
                                    <Mui.Box sx={{ mt: 2 }}>
                                        <Mui.FormControl size="small" sx={{ minWidth: 250 }}>
                                            <Mui.InputLabel id="add-program-pref-label">Add Scholars Program</Mui.InputLabel>
                                            <Mui.Select
                                                labelId="add-program-pref-label"
                                                value=""
                                                label="Add Scholars Program"
                                                onChange={(e) => addProgram(e.target.value)}
                                                disabled={isSubmitting}
                                            >
                                                {availableToAdd.map(p => (
                                                    <Mui.MenuItem key={p.subProgramId} value={p.subProgramId}>
                                                        {p.label || p.subProgramId}
                                                    </Mui.MenuItem>
                                                ))}
                                            </Mui.Select>
                                        </Mui.FormControl>
                                    </Mui.Box>
                                )}

                                <Mui.FormControlLabel
                                    sx={{ mt: 2 }}
                                    control={
                                        <Mui.Switch
                                            checked={alternateOptIn}
                                            onChange={(e) => setAlternateOptIn(e.target.checked)}
                                            disabled={isSubmitting}
                                        />
                                    }
                                    label="Alternate Assignment Opt-In"
                                />

                                <Mui.Divider sx={{ my: 2 }} />
                            </Mui.Box>
                        )}

                        <Mui.FormControl fullWidth sx={{ mt: 2 }}>
                            <Mui.InputLabel id="reason-label">Reason</Mui.InputLabel>
                            <Mui.Select
                                disabled={isLoadingOrFinished}
                                labelId="reason-label"
                                value={reason}
                                label="Reason"
                                onChange={e => setReason(e.target.value)}
                                required
                            >
                                {studentRequestReasons?.map?.(opt => (
                                    <Mui.MenuItem key={opt.KEY} value={opt.KEY}>{opt.LABEL}</Mui.MenuItem>
                                ))}
                            </Mui.Select>
                        </Mui.FormControl>
                        <Mui.TextField
                            disabled={isLoadingOrFinished}
                            label="Case Number"
                            fullWidth
                            sx={{ mt: 2 }}
                            value={caseNumber}
                            onChange={e => setCaseNumber(e.target.value.replace(/\D/g, ""))}
                            required={!isAdmin}
                            error={caseNumber.length > 0 && !caseNumberIsValid}
                            helperText={caseNumber.length > 0 && !caseNumberIsValid ? "Case number must be 6-10 digits." : ""}
                        />
                    </>
                )}
            </Mui.DialogContent>
            <Mui.DialogActions className="padding-2">
                {showSuccess || errorMessage ? (
                    <Mui.Button color="primary" variant="contained" onClick={handleClose}>
                        Close
                    </Mui.Button>
                ) : (
                    <>
                        <Mui.Button color="secondary" variant="outlined" onClick={() => handleClose(true)}>Cancel</Mui.Button>
                        <Mui.Button
                            disabled={isSubmitting || !isFormValid}
                            color="primary"
                            variant="contained"
                            onClick={confirmAction}
                        >
                            {isSubmitting && <Mui.CircularProgress size={20} style={{ marginRight: '.5rem' }} />}
                            Continue
                        </Mui.Button>
                    </>
                )}
            </Mui.DialogActions>
        </Mui.Dialog>
    );
};

export default ChangeRequestedDialog;
